import { useEffect } from "react";
import ConceptPage from "../../products/concept/ConceptPage";
import IntelligenceFeedPage from "../../products/intel/IntelligenceFeedPage";
import ViewpointFeedPage from "../../products/viewpoint/ViewpointFeedPage";
import QaFeedPage from "../../products/qa/QaFeedPage";
import CompanionRoomPage from "../../products/companion_room/CompanionRoomPage";
import { showDesktopOnlyTipModal } from "../common/DesktopOnlyTipModal";
import {
  normalizeIntelCommentKind,
  type MembershipProduct,
  type SpaceProfile,
} from "../SpaceType";
import { isH5MobileProductSupported } from "./productServiceUtils";
import { SpaceModuleSubscribeGate } from "./SpaceModuleSubscribeGate";
import { recordProductPageVisit } from "../ProductPageVisitApi";
import "./SpaceProductStyle.css";

export function SpaceProductSection(props: {
  spaceId: string;
  module: MembershipProduct;
  spaceProfile: SpaceProfile | null;
  membershipProducts: MembershipProduct[];
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  onSubscribeScroll?: () => void;
}) {
  const {
    spaceId,
    module,
    spaceProfile,
    membershipProducts,
    isLoggedIn,
    onLoginRequest,
    onSubscribeScroll,
  } = props;

  const supported = isH5MobileProductSupported(module.productKind);

  useEffect(() => {
    if (!supported) {
      showDesktopOnlyTipModal();
    }
  }, [supported, module.spaceProductId]);

  useEffect(() => {
    const pid = module.spaceProductId?.trim();
    if (!pid) return;
    void recordProductPageVisit(spaceId, pid);
  }, [spaceId, module.spaceProductId]);

  if (!supported) {
    return (
      <div className="space-product-page space-product-page--unsupported">
        <div className="space-product-unsupported-hint">
          <p>该功能暂不支持手机浏览</p>
          <button type="button" className="space-product-unsupported-btn" onClick={showDesktopOnlyTipModal}>
            查看说明
          </button>
        </div>
      </div>
    );
  }

  if (!module.userSubscribed) {
    return (
      <SpaceModuleSubscribeGate
        spaceId={spaceId}
        row={module}
        isLoggedIn={isLoggedIn}
        onLoginRequest={onLoginRequest}
        onSubscribeScroll={onSubscribeScroll}
        spaceProfile={spaceProfile}
      />
    );
  }

  switch (module.productKind) {
    case "viewpoint": {
      const conceptSpaceProductId =
        membershipProducts.find((p) => p.productKind === "concept")?.spaceProductId?.trim() ||
        undefined;
      return (
        <div className="space-product-page">
          <ViewpointFeedPage
            spaceId={spaceId}
            spaceProductId={module.spaceProductId}
            spaceDisplayName={module.displayName?.trim() || ""}
            conceptSpaceProductId={conceptSpaceProductId}
          />
        </div>
      );
    }
    case "qa":
      return (
        <div className="space-product-page">
          <QaFeedPage
            spaceId={spaceId}
            spaceProductId={module.spaceProductId}
            displayName={module.displayName?.trim() || "问答"}
            qaAllowPublic={module.qaAllowPublic === true}
          />
        </div>
      );
    case "concept":
      return (
        <div className="space-product-page">
          <ConceptPage
            spaceId={spaceId}
            spaceProductId={module.spaceProductId}
            displayName={module.displayName?.trim() || "概念选股"}
          />
        </div>
      );
    case "intel":
      return (
        <div className="space-product-page">
          <IntelligenceFeedPage
            spaceId={spaceId}
            spaceProductId={module.spaceProductId}
            displayName={module.displayName?.trim() || "情报"}
            spaceDisplayName={spaceProfile?.name?.trim() || "本空间"}
            intelCommentKind={normalizeIntelCommentKind(module.intelCommentKind)}
          />
        </div>
      );
    case "group_companion":
      return (
        <div className="space-product-page space-product-page--companion">
          <CompanionRoomPage
            spaceId={spaceId}
            spaceProductId={module.spaceProductId}
            productDisplayName={module.displayName}
            spaceDisplayName={spaceProfile?.name?.trim()}
          />
        </div>
      );
    default:
      return null;
  }
}

/** 根据 spaceProductId 查找模块（含订阅态） */
export function findMembershipProduct(
  products: MembershipProduct[],
  spaceProductId: string
): MembershipProduct | null {
  const id = spaceProductId.trim();
  if (!id) return null;
  return products.find((p) => p.spaceProductId === id) ?? null;
}

/** 产品入口点击：不支持则弹框并返回 false */
export function tryOpenH5ProductService(
  product: MembershipProduct | null | undefined
): product is MembershipProduct {
  if (!product) {
    return false;
  }
  if (!isH5MobileProductSupported(product.productKind)) {
    showDesktopOnlyTipModal();
    return false;
  }
  return true;
}
